// src/pages/ZodiacPage.jsx
import React, { useState, useRef } from 'react';
import ZodiacDetailModal from '../components/ZodiacDetailModal';
import SignFinder from '../components/SignFinder';
import CompatibilityComparator from '../components/CompatibilityComparator';
import AscendantCalculator from '../components/AscendantCalculator';
import { WandIcon } from '../components/ui/Icons';
import useScrollReveal from '../utils/useScrollReveal';
import zodiacData from '../data/zodiac-data.json';

const ZodiacPage = ({ onBack, aiStatus, userDetails }) => {
  const [selectedSign, setSelectedSign] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterElement, setFilterElement] = useState('all');
  const [openTopicId, setOpenTopicId] = useState(null);
  const zodiacGridRef = useRef(null);

  const elementLabels = {
    all: '✦ Todos',
    Fuego: '🔥 Fuego',
    Tierra: '🌿 Tierra',
    Aire: '💨 Aire',
    Agua: '💧 Agua',
  };

  const filteredSigns = zodiacData.signs.filter((sign) => {
    const matchesSearch = sign.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesElement = filterElement === 'all' || sign.element === filterElement;
    return matchesSearch && matchesElement;
  });

  useScrollReveal(zodiacGridRef, '.scroll-reveal', [filteredSigns.length]);

  return (
    <div className="page-container">
      <div className="guide-header">
        <h2 className="page-title font-cinzel">Zodiaco y Astrología</h2>
        <p className="page-subtitle">{zodiacData.intro}</p>
      </div>

      {/* Herramientas Interactivas */}
      <div className="zodiac-tools-section">
        <h3 className="zodiac-section-title font-cinzel zodiac-section-title--icon">
          <WandIcon className="zodiac-section-icon" aria-hidden="true" />
          Herramientas Interactivas
        </h3>
        <div className="zodiac-tools-grid">
          <SignFinder aiStatus={aiStatus} userDetails={userDetails} onViewFullSign={setSelectedSign} />
          <CompatibilityComparator />
          <AscendantCalculator onViewFullSign={setSelectedSign} />
        </div>
      </div>

      {/* Fundamentos: Elementos */}
      <div className="zodiac-fundamentals">
        <h3 className="zodiac-section-title font-cinzel">Los Cuatro Elementos</h3>
        <div className="zodiac-elements-grid">
          {zodiacData.elements.map((el) => (
            <div key={el.id} className="zodiac-element-card">
              <div className="zodiac-element-symbol">{el.symbol}</div>
              <h4 className="zodiac-element-name font-cinzel">{el.name}</h4>
              <p className="zodiac-element-signs">{el.signs.join(' · ')}</p>
              <p className="zodiac-element-desc">{el.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="guide-filters">
        <div className="guide-search-wrapper">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Buscar signo por nombre..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="guide-search-input"
          />
          {searchTerm && (
            <button className="search-clear" onClick={() => setSearchTerm('')}>✕</button>
          )}
        </div>
        <div className="guide-suit-filter">
          {Object.keys(elementLabels).map((el) => (
            <button
              key={el}
              onClick={() => setFilterElement(el)}
              className={`suit-tab ${filterElement === el ? 'suit-tab--active' : ''}`}
            >
              {elementLabels[el]}
            </button>
          ))}
        </div>
      </div>

      <p className="guide-count">{filteredSigns.length} {filteredSigns.length === 1 ? 'signo' : 'signos'}</p>

      {/* Signs Grid */}
      <div className="zodiac-grid" ref={zodiacGridRef}>
        {filteredSigns.map((sign) => (
          <button
            key={sign.id}
            className="zodiac-card scroll-reveal"
            onClick={() => setSelectedSign(sign)}
          >
            <div className="zodiac-card-symbol">{sign.symbol}</div>
            <h3 className="zodiac-card-name font-cinzel">{sign.name}</h3>
            <p className="zodiac-card-dates">{sign.dateRange}</p>
            <p className="zodiac-card-element">{sign.element} · {sign.modality}</p>
          </button>
        ))}
      </div>

      {filteredSigns.length === 0 && (
        <div className="guide-empty">
          <p>🔍 No se encontraron signos para "{searchTerm}"</p>
          <button className="main-button secondary-button" onClick={() => { setSearchTerm(''); setFilterElement('all'); }}>
            Limpiar filtros
          </button>
        </div>
      )}

      {/* Fundamentos Avanzados */}
      <div className="zodiac-fundamentals zodiac-fundamentals--topics">
        <h3 className="zodiac-section-title font-cinzel">Fundamentos Avanzados de Astrología</h3>
        <div className="zodiac-accordion">
          {zodiacData.advancedTopics.map((topic) => {
            const isOpen = openTopicId === topic.id;
            return (
              <div key={topic.id} className={`zodiac-accordion-item ${isOpen ? 'zodiac-accordion-item--open' : ''}`}>
                <button
                  className="zodiac-accordion-header"
                  onClick={() => setOpenTopicId(isOpen ? null : topic.id)}
                >
                  <span>{topic.title}</span>
                  <span className="zodiac-accordion-icon">{isOpen ? '−' : '+'}</span>
                </button>
                {isOpen && <p className="zodiac-accordion-body">{topic.content}</p>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Zodiaco Chino */}
      <div className="zodiac-fundamentals">
        <h3 className="zodiac-section-title font-cinzel">Bonus: Zodiaco Chino</h3>
        <p className="zodiac-chinese-intro">{zodiacData.chineseZodiac.intro}</p>
        <p className="zodiac-chinese-intro">{zodiacData.chineseZodiac.elementsNote}</p>
        <div className="chinese-zodiac-grid">
          {zodiacData.chineseZodiac.animals.map((animal) => (
            <div key={animal.id} className="chinese-zodiac-card">
              <div className="chinese-zodiac-symbol">{animal.symbol}</div>
              <h4 className="chinese-zodiac-name font-cinzel">{animal.name}</h4>
              <p className="chinese-zodiac-years">{animal.years.join(' · ')}</p>
              <p className="chinese-zodiac-element">Elemento fijo: {animal.element}</p>
              <p className="chinese-zodiac-traits">{animal.traits.join(' · ')}</p>
              <p className="chinese-zodiac-compat">💫 {animal.compatible.join(' y ')} · ⚡ {animal.challenging}</p>
            </div>
          ))}
        </div>
      </div>

      {selectedSign && (
        <ZodiacDetailModal
          sign={selectedSign}
          compatibilityList={zodiacData.compatibilityMatrix[selectedSign.name]}
          onClose={() => setSelectedSign(null)}
        />
      )}

      <button onClick={onBack} className="main-button secondary-button back-btn">
        ← Volver al Inicio
      </button>
    </div>
  );
};

export default ZodiacPage;
